import React from 'react'
import { Column } from 'react-table'
import styled from 'styled-components'
import get from 'lodash/get'
import { Image } from '../components/Image'
import {
  GateFeedColumnKey,
  GateObservation,
  OBSERVATION_KEYS,
  OBSERVATION_SECTION_KEYS,
  ObservationColumn,
  ObservationKey,
  ObservationSectionKey,
  SectionKey,
  SortingAction,
  SortingReturn,
  TABLE_KEYS,
} from '../models/client/observation'
import { ObservationSort } from '../clients/type'
import { utcToDisplayDate, utcToDistanceDays, utcToMilitaryDisplayTime } from '../util/datetime'
import { text } from '../styles/mixins/text'
import { Button } from '../components/Button'
import { SortDirections } from '../models/tools'
import { Icon } from '../components/Icon/index'
import { Spacer } from '../components/Spacer'
import { color } from '../styles'

const YARD_REPORT_COLUMNS = {
  bare: {
    label: 'Chassis Is Bare',
    key: TABLE_KEYS.isBare,
    hasSort: true,
  },
}

export type ViewableImage = {
  images: string[]
  path: string
  imagePaths: string[]
  observationId: string
  timezone?: string
}

type TableAction = {
  sort?: {
    [X in ObservationSectionKey]?: {
      [Y in GateFeedColumnKey]?: SortingReturn
    }
  }
}

type Feeds = 'gatefeed' | 'yardreport'

type ColumnBuilder = (page: Feeds, actions?: TableAction) => ObservationColumn['columns']

export const handleSortInteraction = (
  columnKey: ObservationKey,
  onSort: SortingAction | undefined,
  currentSort: ObservationSort | null
): SortingReturn => {
  return {
    getCurrentSort: () => {
      if (!currentSort || currentSort?.column !== columnKey) return null
      if (currentSort.column !== columnKey) return null
      return currentSort.direction
    },
    onSelect: (nextDirection) => {
      onSort?.(
        nextDirection
          ? {
              column: columnKey,
              direction: nextDirection,
            }
          : null
      )
    },
  }
}

const getAssetColumns: ColumnBuilder = () => [
  {
    label: 'Picture',
    key: TABLE_KEYS.assetPreviewUrl,
  },
  {
    label: 'Date',
    key: 'DATE',
    altAccessor: TABLE_KEYS.assetTimestamp,
    hasSort: true,
  },
  {
    label: 'Time',
    key: 'TIME',
    altAccessor: TABLE_KEYS.assetTimestamp,
    hasSort: true,
  },
  {
    label: 'Direction',
    key: TABLE_KEYS.assetDirection,
    hasSort: true,
  },
]

const getLoadColumns: ColumnBuilder = (_, actions) => {
  return [
    {
      label: 'Load Type',
      key: TABLE_KEYS.loadType,
      hasSort: true,
    },
    {
      label: 'Load #',
      key: TABLE_KEYS.loadNumber,
      hasSort: true,
    },
    {
      label: 'Seal',
      key: TABLE_KEYS.hasSeal,
      hasSort: true,
    },
    {
      label: 'Seal #',
      key: TABLE_KEYS.sealNumber,
    },
    {
      label: 'Load LP #',
      key: TABLE_KEYS.loadLicensePlate,
      hasSort: true,
    },
    // {
    //     label: 'Load State',
    //     key: TABLE_KEYS.loadState,
    // },
    // FIXME: replace
    {
      label: 'BCO',
      key: TABLE_KEYS.loadCustomerName,
    },
    // {
    //     label: 'Load Color',
    //     key: TABLE_KEYS.loadColor,
    // },
    {
      label: 'Load Damage',
      key: TABLE_KEYS.loadDamage,
      hasSort: true,
    },
    {
      label: 'Load Dwell Time',
      key: TABLE_KEYS.loadDwellTimeHr,
      sorting: actions?.sort?.CONTAINER?.loadDwellTimeHr,
      hasSort: true,
    },
    {
      label: 'Load Remarks',
      key: TABLE_KEYS.loadRemarks,
    },
  ]
}

const getChassisColumns: ColumnBuilder = (page, actions) => {
  return [
    {
      label: 'Chassis #',
      key: TABLE_KEYS.chassisNumber,
      hasSort: true,
    },
    {
      label: 'Chassis LP #',
      key: TABLE_KEYS.chassisLicensePlate,
      hasSort: true,
    },
    // {
    //     label: 'Chassis State',
    //     key: TABLE_KEYS.chassisState,
    // },
    {
      label: 'Chassis Damage',
      key: TABLE_KEYS.chassisDamage,
      hasSort: true,
    },
    ...(page === 'yardreport' ? [YARD_REPORT_COLUMNS.bare] : []),
    {
      label: 'Chassis Dwell Time',
      key: TABLE_KEYS.chassisDwellTimeHr,
      sorting: actions?.sort?.CHASSIS?.chassisDwellTimeHr,
      hasSort: true,
    },
    {
      label: 'Chassis Remarks',
      key: TABLE_KEYS.chassisRemarks,
    },
  ]
}

const getVehicleColumns: ColumnBuilder = (dataSet, actions) => {
  return [
    {
      label: 'Vehicle Type',
      key: TABLE_KEYS.vehicleType,
      hasSort: true,
    },
    {
      label: 'Carrier',
      key: TABLE_KEYS.vehicleCarrierName,
      hasSort: true,
    },
    {
      label: 'Vehicle Truck #',
      key: TABLE_KEYS.vehicleNumber,
      hasSort: true,
    },
    {
      label: 'Vehicle DOT #',
      key: TABLE_KEYS.vehicleDotNumber,
      hasSort: true,
    },
    {
      label: 'Vehicle LP #',
      key: TABLE_KEYS.vehicleLicensePlate,
      hasSort: true,
    },
    // {
    //     label: 'Vehicle State',
    //     key: TABLE_KEYS.vehicleState,
    // },
    // {
    //     label: 'Vehicle Color',
    //     key: TABLE_KEYS.vehicleColor,
    // },
    {
      label: 'Vehicle Damage',
      key: TABLE_KEYS.vehicleDamage,
      hasSort: true,
    },
    {
      label: 'Vehicle Dwell Time',
      key: TABLE_KEYS.vehicleDwellTimeHr,
      sorting: actions?.sort?.VEHICLE?.vehicleDwellTimeHr,
      hasSort: true,
    },
    {
      label: 'Vehicle Remarks',
      key: TABLE_KEYS.vehicleRemarks,
    },
    {
      label: 'Vehicle Class',
      key: TABLE_KEYS.vehicleWeightClass,
      hasSort: true,
    },
    {
      label: 'Vehicle Fuel Type',
      key: TABLE_KEYS.vehicleFuelType,
      hasSort: true,
    },
  ]
}

export const getObservationTemplate = (
  page: 'gatefeed' | 'yardreport',
  actions?: TableAction
): Array<ObservationColumn> => {
  return [
    {
      label: '',
      key: OBSERVATION_SECTION_KEYS.ASSET,
      columns: getAssetColumns(page, actions),
    },
    {
      label: 'Load',
      key: OBSERVATION_SECTION_KEYS.CONTAINER,
      columns: getLoadColumns(page, actions),
    },
    {
      label: 'Chassis',
      key: OBSERVATION_SECTION_KEYS.CHASSIS,
      columns: getChassisColumns(page, actions),
    },
    {
      label: 'Vehicle',
      key: OBSERVATION_SECTION_KEYS.VEHICLE,
      columns: getVehicleColumns(page, actions),
    },
    {
      label: '',
      key: OBSERVATION_SECTION_KEYS.OPERATIONS,
      columns: [
        {
          label: '',
          key: 'EDIT_OPTIONS',
        },
      ],
    },
  ]
}

export const REPORT_TEMPLATE = getObservationTemplate('yardreport')

const COLUMN_KEY_TO_SECTION_KEY = REPORT_TEMPLATE.reduce((draft, section) => {
  section.columns.forEach(({ key }) => {
    draft[key] = section.key
  })
  return draft
}, {} as Record<GateFeedColumnKey, SectionKey>)

const renderValue = (value?: string | null | undefined): string =>
  typeof value === 'string' ? value : '-'

type ColumnDirectory = { [key in GateFeedColumnKey]?: Column<GateObservation> }

export const getColumnDirectory = (params?: {
  actions?: { onViewImage?: (image: ViewableImage) => void; onSort?: SortingAction }
  state?: {
    sort?: ObservationSort | null
  }
  renderExitStatus?: boolean
}): ColumnDirectory => {
  const { actions, state, renderExitStatus } = params || {}
  const directory: { [key in GateFeedColumnKey]?: Column<GateObservation> } = {}
  const data = [
    ...getAssetColumns('yardreport'),
    ...getLoadColumns('yardreport'),
    ...getChassisColumns('yardreport'),
    ...getVehicleColumns('yardreport'),
  ]
  data.forEach((column) => {
    if (column.key === OBSERVATION_KEYS.assetPreviewUrl) {
      directory[column.key] = {
        Header: 'Picture',
        id: OBSERVATION_KEYS.assetPreviewUrl,
        accessor: ({
          tags,
          assetImagePaths,
          assetImageUrls,
          assetPreviewUrl,
          observationId,
          timezone,
        }) => {
          const images = assetImageUrls || []
          const previewUrl = assetPreviewUrl
          const imagePaths = assetImagePaths
          const filePath = tags.includes('v2') ? imagePaths?.[0] : `${imagePaths?.[0]}_t`

          return (
            <Image
              path={previewUrl || undefined}
              fileName={images[0]}
              filePath={filePath}
              description="observation preview"
              // disableClick={!images.length || !previewUrl}
              onClick={(e) => {
                e.stopPropagation()
                // if (!images.length || !previewUrl) return
                actions?.onViewImage?.({
                  images,
                  path: previewUrl,
                  imagePaths,
                  observationId,
                  timezone,
                })
              }}
            />
          )
        },
      }
      return
    }

    if (column.key === 'EDIT_OPTIONS') {
      directory[column.key] = {
        Header: '',
        id: column.key,
        accessor: () => '',
      }
      return
    }

    let Header: string | JSX.Element = column.label
    let accessor = null
    if (column.key === 'DATE') {
      accessor = ({ assetTimestamp, timezone }) =>
        utcToDisplayDate(renderValue(assetTimestamp), timezone)
    } else if (column.key === 'TIME') {
      accessor = ({ assetTimestamp, timezone }) =>
        utcToMilitaryDisplayTime(renderValue(assetTimestamp), timezone)
    } else {
      accessor = (rowData) => {
        const sectionKey = COLUMN_KEY_TO_SECTION_KEY[column.key]
        let hasLeft = false
        if (sectionKey === 'CONTAINER' && rowData.loadOutwardObservationId) hasLeft = true
        if (sectionKey === 'CHASSIS' && rowData.chassisOutwardObservationId) hasLeft = true
        if (sectionKey === 'VEHICLE' && rowData.vehicleOutwardObservationId) hasLeft = true
        const noVal = '-'
        let value = get(rowData, [column.key], noVal) as string
        const timestamp = rowData.assetTimestamp
        const calculateDwell = (outwardId?: string | null, date: string | null = null): string => {
          if (outwardId && !date) return noVal
          if (!timestamp) return noVal
          const timeLabel = utcToDistanceDays(timestamp, date)
          return timeLabel || noVal
        }

        const secondsToHms = (seconds: number | null) => {
          if (seconds == null) return null
          seconds = Number(seconds)
          const d = Math.floor(seconds / (3600 * 24))
          const h = Math.floor((seconds % (3600 * 24)) / 3600)
          const m = Math.floor((seconds % 3600) / 60)

          const dDisplay = d > 0 ? d + (d == 1 ? ' day, ' : ' days, ') : ''
          const hDisplay = h > 0 ? h + (h == 1 ? ' hr, ' : ' hrs, ') : ''
          const mDisplay = m > 0 ? m + (m == 1 ? ' min ' : ' mins ') : ''
          return dDisplay + hDisplay + mDisplay
        }

        const getDwellTime = (type: string, rowData) => {
          switch (type) {
            case 'load':
              return secondsToHms(rowData.loadDwellTimeHr)
            case 'chassis':
              return secondsToHms(rowData.chassisDwellTimeHr)
            case 'vehicle':
              return secondsToHms(rowData.vehicleDwellTimeHr)
          }
        }

        if (column.key === TABLE_KEYS.isBare) {
          value = value ? 'Yes' : 'No'
        }
        if (column.key === TABLE_KEYS.chassisDwellTimeHr && rowData.assetDirection === 'ingate') {
          value = getDwellTime('chassis', rowData)
        }
        if (column.key === TABLE_KEYS.vehicleDwellTimeHr && rowData.assetDirection === 'ingate') {
          value = getDwellTime('vehicle', rowData)
        }
        if (column.key === TABLE_KEYS.loadDwellTimeHr && rowData.assetDirection === 'ingate') {
          value = getDwellTime('load', rowData)
        }

        if (renderExitStatus && hasLeft) {
          return <SectionHasLeft>{value}</SectionHasLeft>
        }
        return value
      }
    }
    if (column.hasSort) {
      const { getCurrentSort, onSelect } = handleSortInteraction(
        column.key,
        actions?.onSort,
        state?.sort || null
      )
      const currentSort = getCurrentSort()
      Header = (
        <ContentRow>
          <Button
            type="text"
            size="small"
            onClick={() => {
              let nextSort: null | SortDirections = null
              if (!currentSort) nextSort = 'DESC'
              if (currentSort === 'DESC') nextSort = 'ASC'
              if (currentSort === 'ASC') nextSort = null
              onSelect?.(nextSort)
            }}
            label={
              // eslint-disable-next-line react/jsx-wrap-multilines
              <ContentRow>
                {column.label}
                <Spacer size={2} />
                {currentSort === 'ASC' ? (
                  <Icon iconKey="UpArrow" color="secondary" size={3} />
                ) : (
                  <div />
                )}
                {currentSort === 'DESC' ? (
                  <Icon iconKey="DownArrow" color="secondary" size={3} />
                ) : (
                  <div />
                )}
                {!currentSort && <DefaultSortIcon />}
              </ContentRow>
            }
          />
        </ContentRow>
      )
    }

    directory[column.key] = {
      Header,
      id: column.key,
      accessor,
    }
  })
  return directory
}

const ContentRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  flex: 1 1 auto;
  white-space: pre-wrap;
  ${text('label', 'primary', 'text', 'bold')}
`

const DefaultSortIcon = styled(Icon).attrs({
  iconKey: 'DefaultArrows',
  color: 'primary',
  activeColor: 'muted',
})`
  svg {
    min-width: 20px;
  }
`

const SectionHasLeft = styled.div`
  color: transparent;
`
